import {
  useState,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { cx } from './cx.js';
import { useScrollFade } from './scroll-fade.js';
import { ChannelIcon, Icon, type IconName } from './icon.js';

export { cx } from './cx.js';

/**
 * `default` is the secondary outline, `primary` and `brand` are the navy action
 * (one per view), `quiet` has no edge until hovered, `danger` is red text (D24).
 */
export type ButtonVariant = 'default' | 'primary' | 'brand' | 'quiet' | 'danger' | 'warning';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  small?: boolean;
  icon?: IconName;
  /**
   * Working: a spinner replaces the icon, the label stays (so the width does not
   * jump), the button is busy and cannot be pressed twice.
   */
  loading?: boolean;
}

/** Name a button by what happens: "Send invite", never "OK" (Button README). */
export function Button({
  variant = 'default',
  small = false,
  icon,
  loading = false,
  className,
  type = 'button',
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx('btn', variant !== 'default' && variant, small && 'small', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="spinner" aria-hidden="true" /> : icon && <Icon name={icon} />}
      {children}
    </button>
  );
}

export function TextLink({ className, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <a className={cx('link', className)} {...rest} />;
}

/**
 * ADR 252: a link that leaves the portal (Stripe, Meta, Google, a clinic's booking
 * page, a partner offer). It always opens in a new tab with no opener, and says
 * so to screen readers, so the portal tab is never navigated away.
 */
export function ExternalLink({
  className,
  children,
  ...rest
}: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'target' | 'rel'>) {
  return (
    <a className={cx('link', className)} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

/**
 * Semantic tones. `laura` is Laura's own violet (her replies, drafts and
 * suggestions); `brand` is the navy tint (a current plan, an applied choice).
 * `violet` stays for the older call sites (D25).
 */
export type Tone = 'blue' | 'amber' | 'red' | 'green' | 'violet' | 'grey' | 'laura' | 'brand';

/** A state in one or two words. Colour never carries meaning alone: the word does. */
export function Badge({
  tone = 'grey',
  className,
  children,
}: {
  tone?: Tone;
  className?: string;
  children: ReactNode;
}) {
  return <span className={cx('badge', `b-${tone}`, className)}>{children}</span>;
}

/** The canvas calls badges "pills"; same component. */
export const Pill = Badge;

export type Channel = 'whatsapp' | 'instagram' | 'messenger' | 'sms' | 'webchat' | 'voice';

const CHANNEL_LABEL: Record<Channel, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  messenger: 'Messenger',
  sms: 'SMS',
  webchat: 'Website chat',
  voice: 'Phone',
};
const CHANNEL_CLASS: Record<Channel, string> = {
  whatsapp: 'ch-whatsapp',
  instagram: 'ch-instagram',
  messenger: 'ch-messenger',
  sms: 'ch-sms',
  webchat: 'ch-web',
  voice: 'ch-voice',
};

/** The channel name people read ("Instagram", "Phone"), for text outside a badge. */
export function channelLabel(channel: Channel): string {
  return CHANNEL_LABEL[channel];
}

/**
 * Where a conversation or contact came from: the channel's mark and its name
 * (ADR 183, D42; the word always stays, D1). `compact` drops the pill for a
 * list row or a meta line: the mark at 14 px in the brand colour, the name in
 * the row's text colour.
 */
export function ChannelBadge({
  channel,
  compact = false,
}: {
  channel: Channel;
  compact?: boolean;
}) {
  return (
    <span
      className={cx(compact ? 'ch-compact' : `badge ${CHANNEL_CLASS[channel]}`, `ch-${channel}`)}
    >
      <ChannelIcon channel={channel} size={compact ? 14 : 12} />
      {CHANNEL_LABEL[channel]}
    </span>
  );
}

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-pressed'> {
  pressed: boolean;
  /** How many items the filter matches, shown after the label ("Needs reply · 3"). */
  count?: number | string;
}

/** A filter that toggles at once. The count is part of the name ("Needs reply 3"). */
export function Chip({ pressed, count, className, children, ...rest }: ChipProps) {
  return (
    <button type="button" aria-pressed={pressed} className={cx('chip', className)} {...rest}>
      {children}
      {count !== undefined && (
        <span className="chip-count">
          {typeof count === 'number' ? count.toLocaleString('en-AU') : count}
        </span>
      )}
    </button>
  );
}

export function Chips({ label, children }: { label: string; children: ReactNode }) {
  const fade = useScrollFade<HTMLDivElement>();
  return (
    <div ref={fade} className="chips" role="group" aria-label={label}>
      {children}
    </div>
  );
}

/**
 * D47: filters in two tiers. The first ones share one row at equal width (two rows
 * of two in a narrow list); a chip's count shows as a badge on its corner and stays
 * in its accessible name. The rest sit on a quiet "More" line under it.
 */
export function FilterRow({
  label,
  children,
  more,
}: {
  label: string;
  children: ReactNode;
  more?: ReactNode;
}) {
  return (
    <div className="filter-row" role="group" aria-label={label}>
      <div className="filter-main">{children}</div>
      {more && (
        <div className="filter-more">
          <span className="filter-more-label">More:</span>
          {more}
        </div>
      )}
    </div>
  );
}

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  actions?: ReactNode;
  /** No padding: a table or list that runs to the card's edge. */
  flush?: boolean;
}

export function Card({ title, actions, flush = false, className, children, ...rest }: CardProps) {
  return (
    <section className={cx('card', flush && 'flush', className)} {...rest}>
      {(title || actions) && (
        <div className="spread card-head">
          {title && <h2>{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * A stable avatar colour for a record (D31): the same id always gets the same
 * of the four tints, so a patient looks the same in the Inbox and the profile.
 */
export function avatarVariant(seed: string): 1 | 2 | 3 | 4 {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return ((h % 4) + 1) as 1 | 2 | 3 | 4;
}

/**
 * Initials in a disc. Decorative: the name always appears beside it.
 * `sm` 24 px (beside a name in a thread, feature 95), `md` 36 px (lists), `lg` 56 px (a side panel), `xl` 64 px (a record's header).
 */
export function Avatar({
  name,
  variant = 1,
  size = 'md',
  src,
  channel,
}: {
  name: string;
  variant?: 1 | 2 | 3 | 4;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Feature 82 (ADR 171): the contact's profile photo (a short-lived link).
   * Decorative, like the initials; a missing or failed image shows the initials.
   */
  src?: string | null | undefined;
  /** ADR 183 (D42): the channel's mark on the corner, for a list row. Decorative: the row names the channel too. */
  channel?: Channel;
}) {
  const [failed, setFailed] = useState<string | undefined>(undefined);
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  const photo = src && failed !== src ? src : undefined;
  return (
    <span
      className={cx(
        'avatar',
        variant > 1 && `a${variant}`,
        size !== 'md' && `avatar-${size}`,
        photo && 'avatar-photo',
      )}
      aria-hidden="true"
    >
      {photo ? (
        <img
          src={photo}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setFailed(photo)}
        />
      ) : (
        initials
      )}
      {channel && (
        <span className={cx('avatar-ch', `ch-${channel}`)}>
          <ChannelIcon channel={channel} size={12} />
        </span>
      )}
    </span>
  );
}

/**
 * One partner post (plan 20, ADR 140): the same card on the clinic Dashboard
 * and in the back-office preview. The partner's name always shows beside its
 * logo; the logo itself is decorative. `actions` holds the buttons.
 * Superseded by `SponsoredCard` (ADR 222, D55); kept for one release.
 */
export function PartnerPost({
  id,
  partner,
  logoUrl,
  title,
  body,
  imageUrl,
  imageAlt = '',
  actions,
}: {
  id: string;
  partner: string;
  logoUrl?: string | null;
  title: string;
  body: string;
  imageUrl?: string | null;
  imageAlt?: string;
  actions?: ReactNode;
}) {
  const heading = `partner-post-${id}`;
  return (
    <article className="partner-post" aria-labelledby={heading}>
      <div className="partner-post-head">
        {logoUrl ? (
          <img className="partner-post-logo" src={logoUrl} alt="" />
        ) : (
          <Avatar name={partner} variant={2} />
        )}
        <span className="partner-post-name">{partner}</span>
        <Badge tone="violet">Partner</Badge>
      </div>
      {imageUrl && <img className="partner-post-image" src={imageUrl} alt={imageAlt} />}
      <h3 id={heading}>{title}</h3>
      <p>{body}</p>
      {actions && <div className="row partner-post-actions">{actions}</div>}
    </article>
  );
}

/**
 * A change against the previous period. `direction` is said in words for
 * screen readers ("Up", "Down", "No change"), the arrow is decorative, and
 * `good` picks the colour (a fall in waiting time is good news).
 */
export interface KpiDelta {
  text: string;
  direction: 'up' | 'down' | 'flat';
  good?: boolean;
}

const DIRECTION_WORD = { up: 'Up', down: 'Down', flat: 'No change' } as const;

export function Kpi({
  title,
  value,
  comparison,
  delta,
  sparkline,
  tone,
}: {
  title: string;
  value: ReactNode;
  /** Context in plain words ("Median, all channels"). */
  comparison?: ReactNode;
  delta?: KpiDelta;
  /** A `Sparkline` (or any small figure) beside the value. */
  sparkline?: ReactNode;
  /** `warning` colours the figure when it needs the team (waiting conversations). */
  tone?: 'warning';
}) {
  const good = delta?.good ?? delta?.direction === 'up';
  const deltaTone: Tone = !delta || delta.direction === 'flat' ? 'grey' : good ? 'green' : 'red';
  return (
    <section className="card kpi">
      {/* An h2: a KPI row sits right under the page h1, so an h3 skipped a level (plan 45 phase 8). */}
      <h2 className="kpi-title">{title}</h2>
      <div className="kpi-body">
        <div className={cx('n', tone)}>{value}</div>
        {sparkline && <div className="kpi-spark">{sparkline}</div>}
      </div>
      {delta && (
        <Badge tone={deltaTone} className="delta">
          {delta.direction !== 'flat' && (
            <Icon name={delta.direction === 'up' ? 'arrow-up' : 'arrow-down'} size={16} />
          )}
          <span className="sr-only">{DIRECTION_WORD[delta.direction]}: </span>
          {delta.text}
        </Badge>
      )}
      {comparison && <div className="d">{comparison}</div>}
    </section>
  );
}

export interface Bar {
  label: string;
  value: number | null;
}

/**
 * Figures are always shown as text (BarChart README). A missing figure reads
 * "Not available" (ADR 126, D16), never a build marker. `unit` follows each
 * figure for screen readers ("12 bookings") so the number is never bare.
 */
export function BarChart({
  bars,
  label,
  unit,
  format = (n) => n.toLocaleString('en-AU'),
}: {
  bars: Bar[];
  label: string;
  unit?: string;
  format?: (n: number) => string;
}) {
  const max = Math.max(1, ...bars.map((b) => b.value ?? 0));
  return (
    <div role="list" aria-label={label}>
      {bars.map((b) => (
        <div className="hbar" role="listitem" key={b.label}>
          <span>{b.label}</span>
          <span className="t" style={{ width: `${((b.value ?? 0) / max) * 100}%` }} />
          <span className="wait">
            {b.value == null ? (
              <span className="help">Not available</span>
            ) : (
              <>
                {format(b.value)}
                {unit && <span className="sr-only"> {unit}</span>}
              </>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Inline note next to the thing it is about. `live` says how it is announced:
 * `assertive` interrupts (a warning the person must act on now), `polite` waits
 * (a confirmation, a hint), `off` is static text present from first paint.
 * Default: warnings assertive, the rest polite (D16).
 */
export function Notice({
  kind,
  live,
  children,
}: {
  kind: 'warn' | 'ok' | 'info';
  live?: 'assertive' | 'polite' | 'off';
  children: ReactNode;
}) {
  const mode = live ?? (kind === 'warn' ? 'assertive' : 'polite');
  return (
    <div
      className={cx('notice', kind)}
      role={mode === 'assertive' ? 'alert' : mode === 'polite' ? 'status' : undefined}
    >
      {children}
    </div>
  );
}

/** Why a conversation is with the team. Safety reasons are never shortened. */
export function ReasonBanner({
  kind,
  title,
  children,
}: {
  kind: 'safety' | 'other';
  title: string;
  children: ReactNode;
}) {
  return (
    <div className={`reason ${kind}`} role={kind === 'safety' ? 'alert' : undefined}>
      <b>{title}</b> {children}
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <h2>{title}</h2>
      {children && <p className="sub">{children}</p>}
      {action}
    </div>
  );
}

/** Mockup §3.3: a page opened without access says which role is needed. */
export function NoAccess({ page, role, needed }: { page: string; role: string; needed: string }) {
  return (
    <section className="card noaccess" aria-labelledby="noaccess-title">
      <h1 id="noaccess-title" className="text-h2">
        You don&apos;t have access to {page}
      </h1>
      <p className="sub">
        You are signed in as {role}. {page} needs {needed}. Ask an Owner or Manager if you need it.
      </p>
    </section>
  );
}

export interface TimelineEntry {
  id: string;
  at: string;
  label: ReactNode;
}

export function Timeline({ entries, label }: { entries: TimelineEntry[]; label: string }) {
  return (
    <ol className="timeline" aria-label={label}>
      {entries.map((e) => (
        <li key={e.id}>
          <time>{e.at}</time>
          <span>{e.label}</span>
        </li>
      ))}
    </ol>
  );
}

/** Review aid for the Novations team only; never in a clinic-facing build (ProposalMarker README). */
export function ProposalMarker({ tag, children }: { tag: string; children: ReactNode }) {
  return (
    <div className="proposal">
      <span className="proposal-tag">{tag}</span>
      {children}
    </div>
  );
}
